/**
 * Container for a sort option item.
 * Wraps the press handler to avoid inline function in the sort sheet.
 * @module features/scouting/components/MobileTargetsToolbar/SortOptionItem/SortOptionItemContainer
 */

import React, { memo, useCallback } from "react";

import SortOptionItemView from "./SortOptionItemView";

/** Props for SortOptionItemContainer */
interface SortOptionItemContainerProps {
  /** Sort field value */
  readonly field: string;
  /** Display label for the sort option */
  readonly label: string;
  /** Whether this sort option is currently selected */
  readonly isActive: boolean;
  /** Callback when a sort option is selected */
  readonly onSelect: (field: string) => void;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Optional testID forwarded to the row Pressable (e2e) */
  readonly testID?: string;
}

/**
 * Wraps press handler for a single sort option item.
 * @param root0 - Component props
 * @param root0.field - Sort field value
 * @param root0.label - Sort option label
 * @param root0.isActive - Whether option is selected
 * @param root0.onSelect - Sort selection callback
 * @param root0.isDark - Dark mode flag
 * @param root0.testID - Optional testID forwarded to the row Pressable
 */
const SortOptionItemContainer = ({
  field,
  label,
  isActive,
  onSelect,
  isDark,
  testID,
}: SortOptionItemContainerProps) => {
  const handlePress = useCallback(() => {
    onSelect(field);
  }, [onSelect, field]);

  return (
    <SortOptionItemView
      label={label}
      isActive={isActive}
      onPress={handlePress}
      isDark={isDark}
      testID={testID}
    />
  );
};

SortOptionItemContainer.displayName = "SortOptionItemContainer";

export default memo(SortOptionItemContainer);
